/**
 * Stale-chunk recovery shared by the lazy-import wrapper and the App Router
 * error boundaries.
 *
 * After a deploy, a browser tab still running the previous build requests
 * `/_next/static/chunks/*.js` hashes that no longer exist on the freshly
 * deployed server and fails with a ChunkLoadError. The CDN graveyard
 * (`assetPrefix`) is the primary fix — it keeps old chunks reachable — and this
 * is the client-side safety net for the cases it can't cover (CDN miss, a tab
 * older than the retention window): force one hard reload onto the current
 * build instead of surfacing a dead-end error screen.
 */

const CHUNK_ERROR_PATTERNS = [
  // webpack / native dynamic import
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  // Turbopack (Next 16): "Failed to load chunk <url> from module <id>"
  'failed to load chunk',
  'chunkloaderror',
]

export const isChunkLoadError = (error: unknown): boolean => {
  const message = (
    error instanceof Error ? error.message : String(error ?? '')
  ).toLowerCase()
  const name = error instanceof Error ? error.name.toLowerCase() : ''

  return (
    name === 'chunkloaderror' ||
    CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
  )
}

const extractChunkSignature = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error ?? '')
  // Either an absolute URL (assetPrefix / CDN) or a root-relative /_next/ path.
  const match =
    message.match(/https?:\/\/\S+/i)?.[0] ?? message.match(/\/_next\/\S+/i)?.[0]

  if (!match) {
    return 'unknown-chunk'
  }

  try {
    const url = new URL(match)
    return `${url.pathname}${url.search}`
  } catch {
    return match
  }
}

const getReloadKey = (signature: string) => `chunk-error-reloaded:${signature}`

/**
 * Forces a single hard reload to pick up the current build when a stale chunk
 * fails to load. Guarded by sessionStorage so a chunk that is genuinely gone
 * (e.g. evicted from the CDN) cannot trap the tab in a reload loop.
 *
 * Returns `true` when a reload was scheduled — the caller should then render a
 * neutral placeholder rather than an error, since the page is about to navigate.
 */
export const reloadOnChunkError = (error: unknown): boolean => {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) {
    return false
  }

  const reloadKey = getReloadKey(extractChunkSignature(error))

  if (sessionStorage.getItem(reloadKey) === 'true') {
    // Already reloaded once for this chunk and it still fails: stop retrying and
    // let the error surface. Clear the flag so a later deploy can retry afresh.
    sessionStorage.removeItem(reloadKey)
    return false
  }

  sessionStorage.setItem(reloadKey, 'true')
  window.location.reload()

  return true
}
